import React from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';
import { system, variant } from 'styled-system';

import { deprecated } from '../../internal/propTypeValidators';
import sx from '../../internal/styles/sx';
import BaseText from './BaseText';

// for retro compatibility
const colorMap = {
  positive: {
    color: 'positive.default',
  },
  informative: {
    color: 'informative.default',
  },
  negative: {
    color: 'critical.default',
  },
  white: {
    color: 'white',
  },
};

const StyledText = styled(BaseText)(
  variant({
    prop: 'priority',
    variants: {
      1: {
        color: 'neutrals.1',
        fontSize: 'heading.0',
      },
      2: {
        color: 'neutrals.0',
        fontSize: 'body.2',
      },
      3: {
        color: 'neutrals.2',
        fontSize: 'body.2',
      },
      4: {
        color: 'neutrals.0',
        fontSize: 'body.1',
      },
      5: {
        color: 'neutrals.2',
        fontSize: 'body.1',
      },
      6: {
        color: 'neutrals.1',
        fontSize: 'body.0',
      },
      7: {
        color: 'neutrals.2',
        fontSize: 'body.0',
      },
      8: {
        color: 'neutrals.4',
        fontSize: 'body.0',
      },
    },
  }),
  variant({
    prop: 'preserveWhitespace',
    variants: {
      true: {
        whiteSpace: 'pre',
      },
    },
  }),
  system({
    textAlign: true,
    fontStyle: true,
    textTransform: true,
    fontWeight: {
      property: 'fontWeight',
      scale: 'fontWeights',
    },
    fontSize: {
      property: 'fontSize',
      scale: 'fontSizes',
    },
    lineHeight: {
      property: 'lineHeight',
      scale: 'lineHeights',
    },
  }),
  ({ ellipsis }) =>
    ellipsis &&
    css({
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block', // we must force to block
    }),
  ({ capitalize }) =>
    capitalize &&
    css({
      textTransform: 'capitalize',
    }),
  ({ letterSpacing }) =>
    letterSpacing &&
    css({
      letterSpacing: letterSpacing,
    }),
  ({ themeColor, color }) => {
    const final = colorMap[color || themeColor];
    const c = final ? final.color : color;
    return css({ color: c });
  },
  sx
);

/**
 * @deprecated since version 24.0.0. Please use the typography components instead (Body, Display, Heading and Overline).
 */
const Text = React.forwardRef(({ textAlign = 'left', ...props }, ref) => (
  <StyledText textAlign={textAlign} ref={ref} {...props} />
));

Text.propTypes = {
  as: PropTypes.oneOf(['span', 'p', 'div']),
  priority: deprecated(PropTypes.number),
  themeColor: deprecated(PropTypes.string),
  textAlign: PropTypes.oneOf(['left', 'center', 'right', 'justify']),
  ellipsis: PropTypes.bool,
  capitalize: PropTypes.bool,
  textTransform: PropTypes.string,
  lineHeight: PropTypes.oneOf(['body', 'heading']),
  /**
   *  Any colors tokens defined in the theme like neutrals.0, positive.default and so on.
   *  Also accepts an array of tokens for different screen sizes
   * */
  color: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  /** Preserves the whitespace by setting the white-space:pre attribute  */
  preserveWhitespace: PropTypes.bool,
  fontStyle: PropTypes.oneOf(['normal', 'italic', 'oblique']),
};

export default Text;
